import { Protocol as ControlProtocol } from '@studio/models/control';
import type { Configuration as OnvifConfig } from '@studio/models/control/onvif';
import type { Model } from '@studio/models/device';
import type { Configuration as IrisConfig } from '@studio/models/video/iris';
import type { Configuration as NdiConfig } from '@studio/models/video/ndi';
import pick from 'lodash-es/pick.js';
import type { Discovery } from '#~/device/discovery/index.js';
import { Lang } from '#~/video/index.js';

/** Add Source table row derived from a state-sync discovery. */
export type AddSourceDiscoveryRow = {
  id: Discovery['id'];
  name: string;
  connection: string;
  ip: string;
  bridge_s: string[];
  irisEnabled?: boolean;
  modelId?: Model['id'];
};

export const determineDiscoveryIp = (discovery: Discovery): string => {
  if ('video' in discovery && Lang.protocol[discovery.video?.protocol].toLowerCase() === 'local') {
    return 'N/A';
  }

  if ('video' in discovery && Lang.protocol[discovery.video?.protocol].toLowerCase() === 'ndi') {
    return (discovery.video as NdiConfig).host;
  }

  if ('video' in discovery && Lang.protocol[discovery.video?.protocol].toLowerCase() === 'iris') {
    return (discovery.video as IrisConfig).host;
  }

  if ('control' in discovery) {
    const config = discovery.control;
    if (config?.protocol === ControlProtocol.ONVIF) {
      return (config as OnvifConfig).host;
    }
  }
  return 'N/A';
};

/** Protocol label of a discovery row; also labels the row's transport choices. */
export const discoveryConnection = (discovery: Discovery): string =>
  'video' in discovery ? Lang.protocol[discovery.video.protocol] : 'RTSP (ONVIF)';

/** Map a discovery into the table row the Add Source selector renders. */
export const toAddSourceDiscoveryRow = (
  discovery: Discovery,
  bridgeNames: string[]
): AddSourceDiscoveryRow => ({
  ...pick(discovery, ['id', 'name']),
  connection: discoveryConnection(discovery),
  ip: determineDiscoveryIp(discovery),
  bridge_s: bridgeNames.length > 0 ? bridgeNames : ['Unknown'],
  ...(discovery.irisEnabled ? { irisEnabled: true } : {}),
  ...(discovery.modelId ? { modelId: discovery.modelId } : {})
});
